// apps/client/src/hooks/useEdmContent.ts
//
// Plan-v3 core Slice E1: typed wrappers for the eDM content library
// (edm_content: templates, blocks) and topics (edm_preferences), all served by
// the `edmCampaignAction` callable (Slice B3 dispatches to the Slice D
// handlers). The server types are not importable here, so the minimal shapes
// are mirrored below; the server is the authority for every rule (block
// limits, https-only URLs, sanitising, topic ids).
//
// Nothing in this file is called unless the matching flag helper in
// utils/edm is true; a flag-off page never reaches these actions.

import { useMemo } from 'react';
import { useEdmCompanyCall } from './useEdmCampaigns';
import type { EdmContentPath, EdmFixedLayout } from './useEdmCampaigns';

// ---------------------------------------------------------------- blocks

export type EdmBlockType = 'heading' | 'text' | 'button' | 'image' | 'divider' | 'spacer' | 'twoColumn';

/** Block shapes (mirrors services/client-functions/src/edm/edmBlocks.ts, Slice D1). */
export type EdmBlock =
  | { type: 'heading'; text: string }
  | { type: 'text'; text: string }
  | { type: 'button'; label: string; url: string }
  | { type: 'image'; url: string; alt: string; width?: number | null; linkUrl?: string | null }
  | { type: 'divider' }
  | { type: 'spacer'; height?: number | null }
  | { type: 'twoColumn'; left: string; right: string };

/** Server cap on blocks per email (D1). */
export const EDM_MAX_BLOCKS = 40;
/** Server cap on an image block's width, in email px (D1). */
export const EDM_MAX_IMAGE_WIDTH = 600;

export const EDM_BLOCK_LABEL: Record<EdmBlockType, string> = {
  heading: 'Heading',
  text: 'Text',
  button: 'Button',
  image: 'Image',
  divider: 'Divider',
  spacer: 'Spacer',
  twoColumn: 'Two columns',
};

export const EDM_BLOCK_TYPES: readonly EdmBlockType[] = [
  'heading',
  'text',
  'button',
  'image',
  'divider',
  'spacer',
  'twoColumn',
];

/** An empty block of `type`. */
export function newEdmBlock(type: EdmBlockType): EdmBlock {
  switch (type) {
    case 'heading':
      return { type, text: '' };
    case 'text':
      return { type, text: '' };
    case 'button':
      return { type, label: '', url: '' };
    case 'image':
      return { type, url: '', alt: '', width: null, linkUrl: null };
    case 'divider':
      return { type };
    case 'spacer':
      return { type, height: 24 };
    case 'twoColumn':
      return { type, left: '', right: '' };
    default:
      return { type: 'text', text: '' };
  }
}

/** Only well-formed block lists from the server are edited; anything else is dropped. */
export function normaliseEdmBlocks(raw: unknown): EdmBlock[] {
  if (!Array.isArray(raw)) return [];
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  const out: EdmBlock[] = [];
  for (const b of raw) {
    if (!b || typeof b !== 'object') continue;
    const r = b as Record<string, unknown>;
    switch (r.type) {
      case 'heading':
      case 'text':
        out.push({ type: r.type, text: str(r.text) });
        break;
      case 'button':
        out.push({ type: 'button', label: str(r.label), url: str(r.url) });
        break;
      case 'image':
        out.push({ type: 'image', url: str(r.url), alt: str(r.alt), width: num(r.width), linkUrl: str(r.linkUrl) || null });
        break;
      case 'divider':
        out.push({ type: 'divider' });
        break;
      case 'spacer':
        out.push({ type: 'spacer', height: num(r.height) });
        break;
      case 'twoColumn':
        out.push({ type: 'twoColumn', left: str(r.left), right: str(r.right) });
        break;
      default:
        break;
    }
  }
  return out;
}

// ---------------------------------------------------------------- templates

/** A saved template (`companies/{c}/edm_templates`), ISO strings only. */
export interface EdmTemplate {
  templateId: string;
  name: string;
  updatedAt: string | null;
  subject: string;
  contentPath: EdmContentPath;
  fixedLayout: EdmFixedLayout | null;
  customHtml: string | null;
  blocks?: EdmBlock[] | null;
  /** A built-in starter (read-only). */
  starter?: boolean;
}

export interface EdmSaveTemplateInput {
  /** null/absent = create. */
  templateId?: string | null;
  name: string;
  subject: string;
  contentPath: EdmContentPath;
  fixedLayout: EdmFixedLayout | null;
  customHtml: string | null;
  blocks?: EdmBlock[] | null;
}

// ---------------------------------------------------------------- topics

/** A campaign topic (edm_preferences). The server mints ids for new topics. */
export interface EdmTopic {
  id: string;
  name: string;
}

export interface EdmContentApi {
  listTemplates: () => Promise<{ templates: EdmTemplate[] }>;
  saveTemplate: (input: EdmSaveTemplateInput) => Promise<{ templateId: string }>;
  deleteTemplate: (templateId: string) => Promise<unknown>;
  getTopics: () => Promise<{ topics: EdmTopic[] }>;
  /** Topics without an id are new (the server mints one). Removing a referenced topic refuses topic_in_use. */
  saveTopics: (topics: Array<{ id?: string; name: string }>) => Promise<{ topics: EdmTopic[] }>;
}

/** The content-library surface bound to one company. */
export function useEdmContent(companyId: string | undefined): EdmContentApi {
  const withCompany = useEdmCompanyCall(companyId);

  return useMemo<EdmContentApi>(
    () => ({
      listTemplates: async () => {
        const res = await withCompany<{ templates?: EdmTemplate[] }>('listTemplates');
        return { templates: Array.isArray(res?.templates) ? res.templates : [] };
      },
      saveTemplate: (input) => withCompany('saveTemplate', { ...input }),
      deleteTemplate: (templateId) => withCompany('deleteTemplate', { templateId }),
      getTopics: async () => {
        const res = await withCompany<{ topics?: EdmTopic[] }>('getTopics');
        return { topics: Array.isArray(res?.topics) ? res.topics : [] };
      },
      saveTopics: (topics) => withCompany('saveTopics', { topics }),
    }),
    [withCompany],
  );
}
